/*
 * Mobile filter drawer for the native PLP / search facet sidebar (Boost parity).
 * Below 990px the facet aside renders as a right-side slide-in drawer; this
 * module wires the toolbar FILTER button, close button, backdrop, and Escape.
 *
 * Event delegation is used throughout so everything keeps working after
 * facets.js re-renders the filter groups via AJAX (the aside element itself is
 * never replaced). The "Show results (N)" footer count follows #ProductCount
 * with a MutationObserver.
 */
(() => {
  const OPEN_CLASS = 'facets-drawer-open';

  const getDrawer = () =>
    document.getElementById('main-collection-filters') || document.getElementById('main-search-filters');

  let lastTrigger = null;
  let collapsedOnce = false;

  const isMobile = () => window.matchMedia('(max-width: 989px)').matches;

  const focusableIn = (root) =>
    Array.from(
      root.querySelectorAll('button, [href], input, select, textarea, summary, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.disabled && el.getBoundingClientRect().width > 0);

  const openDrawer = (trigger) => {
    const drawer = getDrawer();
    if (!drawer) return;

    // Boost's mobile drawer starts with every group collapsed, regardless of
    // the desktop expand-by-default setting. Collapse once per page load.
    if (!collapsedOnce && isMobile()) {
      drawer.querySelectorAll('details.facets__disclosure-vertical[open]').forEach((group) => {
        group.removeAttribute('open');
      });
      collapsedOnce = true;
    }

    lastTrigger = trigger || null;
    if (lastTrigger) lastTrigger.setAttribute('aria-expanded', 'true');
    drawer.classList.add('is-open');
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    document.body.classList.add(OPEN_CLASS);

    // The drawer is visibility:hidden until the open transition kicks in, and a
    // hidden element silently refuses focus — wait a frame before moving it.
    const closeButton = drawer.querySelector('[data-facets-drawer-close]');
    if (closeButton) {
      requestAnimationFrame(() => requestAnimationFrame(() => closeButton.focus()));
    }
  };

  const closeDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.removeAttribute('role');
    drawer.removeAttribute('aria-modal');
    document.body.classList.remove(OPEN_CLASS);
    if (lastTrigger) {
      lastTrigger.setAttribute('aria-expanded', 'false');
      lastTrigger.focus();
      lastTrigger = null;
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-facets-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener);
      return;
    }
    if (event.target.closest('[data-facets-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!document.body.classList.contains(OPEN_CLASS)) return;

    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }

    // Trap Tab inside the open drawer (it overlays the page as a modal).
    if (event.key === 'Tab' && isMobile()) {
      const drawer = getDrawer();
      if (!drawer) return;
      const focusable = focusableIn(drawer);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!drawer.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // If the viewport crosses to desktop while the drawer is open, the aside
  // reverts to a static sidebar — clear the drawer state so the page unlocks.
  window.addEventListener('resize', () => {
    if (!isMobile() && document.body.classList.contains(OPEN_CLASS)) closeDrawer();
  });

  // Animate the View More / View Less reveal in the facet lists (HYD-164).
  // Dawn's show-more.js toggles `.hidden` on the overflow items in the button's
  // own click handler; we capture the list height before that runs (capture
  // phase) and FLIP-animate to the new height afterwards (bubble phase).
  const motionOk = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const showMoreList = (button) => {
    const parent = button.closest('.parent-display');
    return parent ? parent.querySelector('ul[role="list"]') : null;
  };
  let showMoreStartHeight = null;

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.button-show-more');
      if (!button || !button.closest('.facets-container') || !motionOk()) return;
      const list = showMoreList(button);
      showMoreStartHeight = list ? list.getBoundingClientRect().height : null;
    },
    true
  );

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.button-show-more');
    if (!button || !button.closest('.facets-container') || showMoreStartHeight === null) return;
    const list = showMoreList(button);
    if (!list) return;

    const endHeight = list.getBoundingClientRect().height;
    const startHeight = showMoreStartHeight;
    showMoreStartHeight = null;
    if (Math.abs(endHeight - startHeight) < 1) return;

    list.style.height = `${startHeight}px`;
    list.style.overflow = 'hidden';
    // Force a reflow so the transition starts from the old height.
    list.getBoundingClientRect();
    list.style.transition = 'height 0.3s ease';
    list.style.height = `${endHeight}px`;
    list.addEventListener(
      'transitionend',
      () => {
        list.style.height = '';
        list.style.overflow = '';
        list.style.transition = '';
      },
      { once: true }
    );
  });

  // Fix the FILTER | SORT bar to the top of the viewport once scrolled past it
  // (Boost parity). The toolbar keeps the bar's height as padding while stuck so
  // the grid doesn't jump when the bar leaves the flow.
  const initStickyToolbar = () => {
    const bar = document.querySelector('.collection-toolbar__bar');
    if (!bar) return;
    const toolbar = bar.parentElement;
    let offset = 0;

    const unstick = () => {
      bar.classList.remove('is-stuck');
      toolbar.style.paddingTop = '';
    };

    const measure = () => {
      const wasStuck = bar.classList.contains('is-stuck');
      if (wasStuck) unstick();
      offset = bar.getBoundingClientRect().top + window.scrollY;
      if (wasStuck) apply();
    };

    const apply = () => {
      const stick = isMobile() && window.scrollY >= offset;
      if (stick && !bar.classList.contains('is-stuck')) {
        toolbar.style.paddingTop = `${bar.offsetHeight}px`;
        bar.classList.add('is-stuck');
      } else if (!stick && bar.classList.contains('is-stuck')) {
        unstick();
      }
    };

    window.addEventListener('scroll', apply, { passive: true });
    window.addEventListener('resize', () => {
      measure();
      apply();
    });
    // Content above the toolbar (hero images, banners) can shift its offset as it loads.
    window.addEventListener('load', () => {
      measure();
      apply();
    });
    measure();
    apply();
  };
  initStickyToolbar();

  // Keep the footer "Show results (N)" in sync with the AJAX-updated count.
  document.addEventListener('DOMContentLoaded', () => {
    const count = document.getElementById('ProductCount');
    const target = document.querySelector('[data-facets-drawer-count]');
    if (!count || !target) return;

    const sync = () => {
      const match = count.textContent.match(/\d[\d,.]*/);
      target.textContent = match ? ` (${match[0]})` : '';
    };
    sync();
    new MutationObserver(sync).observe(count, { childList: true, subtree: true, characterData: true });
  });
})();
